import React, { createContext, useState, useContext, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { createMuiTheme, ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";
import { ptBR } from "@material-ui/core/locale";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../Auth/AuthContext";

import "../../styles/fonts.css";
import {
  lightTokens,
  darkTokens,
  statusPalette,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  spacingUnit,
  controlHeight,
  rem,
  radius,
  motion,
  navy,
  accent,
  neutral
} from "../../theme/tokens";

const ThemeContext = createContext();
const THEME_STORAGE_PREFIX = "rsdeskDarkMode";

/*
 * Escala tipografica do sistema.
 *
 *   h1..h3      -> Cera Pro Black,  CAIXA ALTA  (titulos)
 *   h4..h6      -> Cera Pro Medium, CAIXA ALTA  (subtitulos / titulos curtos)
 *   subtitle    -> Cera Pro Medium, caixa normal
 *   body        -> Cera Pro Regular, caixa normal (leitura longa)
 *
 * Cada nivel traz tamanho, entrelinha e tracking coerentes entre si —
 * antes existiam 17 tamanhos avulsos definidos direto nos componentes.
 */
const buildTypography = () => ({
  fontFamily,
  fontWeightRegular: fontWeight.regular,
  fontWeightMedium: fontWeight.medium,
  fontWeightBold: fontWeight.black,
  htmlFontSize: 16,
  fontSize: 14,

  h1: {
    fontFamily,
    fontSize: fontSize["3xl"],
    fontWeight: fontWeight.black,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.capsTight,
    textTransform: "uppercase"
  },
  h2: {
    fontFamily,
    fontSize: fontSize["2xl"],
    fontWeight: fontWeight.black,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.capsTight,
    textTransform: "uppercase"
  },
  h3: {
    fontFamily,
    fontSize: fontSize.xl,
    fontWeight: fontWeight.black,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.caps,
    textTransform: "uppercase"
  },
  h4: {
    fontFamily,
    fontSize: fontSize.lg,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.caps,
    textTransform: "uppercase"
  },
  h5: {
    fontFamily,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.caps,
    textTransform: "uppercase"
  },
  h6: {
    fontFamily,
    fontSize: fontSize.md,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.caps,
    textTransform: "uppercase"
  },

  subtitle1: {
    fontFamily,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal
  },
  subtitle2: {
    fontFamily,
    fontSize: fontSize.md,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal
  },

  body1: {
    fontFamily,
    fontSize: fontSize.base,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.relaxed,
    letterSpacing: letterSpacing.wide
  },
  body2: {
    fontFamily,
    fontSize: fontSize.md,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.wide
  },

  button: {
    fontFamily,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.capsTight,
    textTransform: "uppercase"
  },
  caption: {
    fontFamily,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.wide
  },
  overline: {
    fontFamily,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.caps,
    textTransform: "uppercase"
  }
});

export const ThemeProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const userThemeKey = user?.id ? `${THEME_STORAGE_PREFIX}:${user.id}` : null;
  const [preference, setPreference] = useState({ key: null, darkMode: false });

  useEffect(() => {
    if (!userThemeKey) {
      setPreference({ key: null, darkMode: false });
      return;
    }

    let storedDarkMode = false;
    try {
      storedDarkMode = localStorage.getItem(userThemeKey) === "true";
    } catch (err) {
      storedDarkMode = false;
    }
    setPreference({ key: userThemeKey, darkMode: storedDarkMode });
  }, [userThemeKey]);

  const isLoginRoute = location.pathname === "/login";
  const darkMode = Boolean(
    !isLoginRoute &&
    userThemeKey &&
    preference.key === userThemeKey &&
    preference.darkMode
  );

  const toggleTheme = useCallback(() => {
    if (!userThemeKey) return;
    setPreference(current => ({
      key: userThemeKey,
      darkMode: current.key === userThemeKey ? !current.darkMode : true,
    }));
  }, [userThemeKey]);

  useEffect(() => {
    if (!userThemeKey || preference.key !== userThemeKey) return;
    try {
      localStorage.setItem(userThemeKey, String(preference.darkMode));
    } catch (err) {
      // Ignore storage errors and keep the in-memory theme.
    }
  }, [preference, userThemeKey]);

  const tokens = darkMode ? darkTokens : lightTokens;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", darkMode ? "dark" : "light");
    Object.entries(tokens).forEach(([key, value]) => {
      if (typeof value !== "string") return;
      root.style.setProperty(`--rs-${key.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`)}`, value);
    });

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", tokens.sidebar);
    }
  }, [darkMode, tokens]);

  const theme = useMemo(
    () =>
      createMuiTheme({
        spacing: spacingUnit,
        palette: {
          type: darkMode ? "dark" : "light",
          primary: {
            main: tokens.primary,
            dark: tokens.primaryHover,
            light: darkMode ? accent[400] : navy[500],
            contrastText: tokens.primaryContrast,
          },
          secondary: {
            main: tokens.accent,
            dark: accent[600],
            light: accent[400],
            contrastText: darkMode ? navy[950] : neutral[0],
          },
          error: { main: tokens.danger },
          warning: { main: tokens.warning },
          success: { main: tokens.success },
          info: { main: tokens.info },
          background: {
            default: tokens.appBackground,
            paper: tokens.surface,
          },
          text: {
            primary: tokens.textPrimary,
            secondary: tokens.textSecondary,
            disabled: tokens.textDisabled,
          },
          divider: tokens.border,
        },
        shape: {
          borderRadius: radius.md,
        },
        typography: buildTypography(),
        scrollbarStyles: {
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: radius.pill,
            backgroundColor: darkMode ? "rgba(255,255,255,0.16)" : neutral[300],
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        },
        custom: {
          tokens,
          radius,
          motion,
          // Altura dos controles, derivada da razao unica em tokens.js.
          controlHeight,
          // Aliases mantidos por compatibilidade com o codigo existente.
          sidebar: tokens.sidebar,
          sidebarStrong: tokens.sidebarStrong,
          sidebarSoft: tokens.sidebarSoft,
          header: tokens.header,
          border: tokens.border,
          surfaceSoft: tokens.surfaceSoft,
          cardShadow: tokens.shadow.md,
          shadow: tokens.shadow,
          status: statusPalette(tokens),
        },
        props: {
          MuiTooltip: {
            arrow: true,
            placement: "top",
            enterDelay: 250,
            enterNextDelay: 100,
          },
          MuiButton: {
            disableElevation: true,
          },
          MuiTextField: {
            variant: "outlined",
            size: "small",
          },
        },
        overrides: {
          MuiCssBaseline: {
            "@global": {
              body: {
                fontFamily,
                backgroundColor: tokens.appBackground,
                color: tokens.textPrimary,
              },
              "strong, b": { fontWeight: fontWeight.medium },
            },
          },
          /*
           * Proporcao dos controles — ver CONTROL_RATIO em tokens.js.
           * A altura vinha de numeros soltos (40/32/48) contra textos
           * pequenos, o que dava razao 3,08 e 3,43: caixa folgada com
           * pouca letra dentro, que e o que faz um botao parecer
           * grande e vazio. Agora a caixa encolhe e o texto cresce.
           */
          /*
           * O botao anda um degrau ABAIXO dos demais controles.
           * =============================================================
           * A razao 2,45 continua valendo para campo, select e aba, que
           * precisam da altura para receber texto digitado. Botao nao:
           * o rotulo e curto e fixo, entao a mesma caixa sobra e e o
           * que deixava as acoes de tela ("ADICIONAR CONTATO",
           * "IMPORTAR PLANILHA") pesando mais que o conteudo.
           *
           * Cada tamanho passa a usar a caixa e a fonte do degrau
           * anterior — medium vira o antigo small, e assim por diante.
           * O icone tambem encolhe: em 20px cru ele mandava mais na
           * altura visual do que o proprio texto.
           * =============================================================
           */
          MuiButton: {
            root: {
              borderRadius: radius.md,
              minHeight: controlHeight.sm,
              fontSize: fontSize.md,
              boxShadow: "none",
              paddingTop: rem(4),
              paddingBottom: rem(4),
              paddingLeft: rem(12),
              paddingRight: rem(12),
              transition: `background-color ${motion.base}, box-shadow ${motion.base}, border-color ${motion.base}`,
            },
            sizeSmall: {
              minHeight: rem(26),
              fontSize: fontSize.sm,
              paddingTop: rem(2),
              paddingBottom: rem(2),
              paddingLeft: rem(8),
              paddingRight: rem(8),
            },
            sizeLarge: {
              minHeight: controlHeight.md,
              fontSize: fontSize.base,
              paddingLeft: rem(16),
              paddingRight: rem(16),
            },
            startIcon: {
              marginRight: rem(6),
              marginLeft: rem(-2),
            },
            endIcon: {
              marginLeft: rem(6),
              marginRight: rem(-2),
            },
            iconSizeSmall: {
              "& > *:first-child": { fontSize: rem(14) },
            },
            iconSizeMedium: {
              "& > *:first-child": { fontSize: rem(16) },
            },
            iconSizeLarge: {
              "& > *:first-child": { fontSize: rem(18) },
            },
            contained: {
              boxShadow: "none",
              "&:hover": { boxShadow: tokens.shadow.sm },
            },
            outlined: {
              borderColor: tokens.borderStrong,
            },
          },
          MuiPaper: {
            root: { backgroundImage: "none" },
            rounded: { borderRadius: radius.lg },
            outlined: { borderColor: tokens.border },
          },
          MuiChip: {
            root: {
              borderRadius: radius.pill,
              fontWeight: fontWeight.medium,
              fontSize: fontSize.sm,
              letterSpacing: letterSpacing.wide,
              // Chip ia para o outro extremo (razao 2,0 = apertado).
              height: 26,
            },
            sizeSmall: {
              height: 22,
              fontSize: fontSize.xs,
            },
          },
          MuiTooltip: {
            tooltip: {
              borderRadius: radius.sm,
              fontSize: fontSize.sm,
              fontWeight: fontWeight.regular,
              maxWidth: 280,
              lineHeight: lineHeight.normal,
              padding: "0.5rem 0.75rem",
              backgroundColor: darkMode ? neutral[100] : navy[850],
              color: darkMode ? navy[900] : neutral[0],
            },
            arrow: {
              color: darkMode ? neutral[100] : navy[850],
            },
          },
          MuiTab: {
            root: {
              textTransform: "uppercase",
              fontWeight: fontWeight.medium,
              fontSize: fontSize.base,
              letterSpacing: letterSpacing.capsTight,
              minHeight: controlHeight.md,
              transition: `color ${motion.base}, background-color ${motion.base}`,
            },
          },
          /*
           * Sem este override o MuiTab acima era INERTE: o MuiTabs
           * mantinha o minHeight 48 em px cru do proprio Material-UI e
           * vencia a altura da aba.
           */
          MuiTabs: {
            root: { minHeight: controlHeight.md },
          },
          /*
           * Componentes que o Material-UI dimensiona em PIXEL CRU e que
           * por isso ficavam de fora do eixo de escala. O caso mais
           * visivel era o IconButton: com padding de 12px fixo, ele
           * renderizava ~42px ao lado de um botao de ~26px na mesma
           * barra. Convertidos para rem, passam a acompanhar o resto.
           *
           * ATENCAO: isto e um fork de valores internos do
           * @material-ui/core@4.x — revalidar ao subir de versao.
           */
          MuiIconButton: {
            root: { padding: rem(9) },
            sizeSmall: { padding: rem(4) },
          },
          MuiListItem: {
            root: {
              paddingTop: rem(8),
              paddingBottom: rem(8),
              paddingLeft: rem(16),
              paddingRight: rem(16),
            },
          },
          MuiMenuItem: {
            root: {
              minHeight: controlHeight.lg,
              paddingTop: rem(6),
              paddingBottom: rem(6),
            },
          },
          MuiAvatar: {
            root: { width: rem(36), height: rem(36) },
          },
          MuiFormControl: {
            marginDense: { marginTop: rem(8), marginBottom: rem(8) },
          },
          MuiFormHelperText: {
            root: {
              marginLeft: 0,
              fontSize: fontSize.sm,
              lineHeight: lineHeight.normal,
            },
          },
          MuiInputLabel: {
            outlined: {
              color: tokens.textSecondary,
              fontWeight: fontWeight.regular,
            },
          },
          MuiOutlinedInput: {
            root: {
              borderRadius: radius.md,
              backgroundColor: darkMode ? tokens.surfaceSunken : neutral[0],
              minHeight: controlHeight.md,
              fontSize: fontSize.base,
              transition: `border-color ${motion.fast}, box-shadow ${motion.fast}, background-color ${motion.fast}`,
              "&:hover $notchedOutline": {
                borderColor: tokens.borderStrong,
              },
              "&$focused $notchedOutline": {
                borderColor: tokens.primary,
                borderWidth: 1,
              },
              "&$focused": {
                boxShadow: `0 0 0 3px ${tokens.primarySoft}`,
              },
            },
            notchedOutline: {
              borderColor: tokens.border,
            },
          },
          MuiTableCell: {
            root: {
              borderBottom: `1px solid ${tokens.border}`,
              fontSize: fontSize.md,
              padding: "0.75rem 1rem",
            },
            head: {
              color: tokens.textSecondary,
              fontWeight: fontWeight.medium,
              backgroundColor: tokens.surfaceSoft,
              borderBottom: `1px solid ${tokens.border}`,
              fontSize: fontSize.xs,
              textTransform: "uppercase",
              letterSpacing: letterSpacing.caps,
              whiteSpace: "nowrap",
            },
            sizeSmall: {
              padding: "0.5rem 0.75rem",
            },
          },
          MuiTableRow: {
            root: {
              transition: `background-color ${motion.fast}`,
              "&:hover": {
                backgroundColor: tokens.surfaceSoft,
              },
            },
          },
          MuiDialog: {
            paper: {
              borderRadius: radius.xl,
              border: `1px solid ${tokens.border}`,
              boxShadow: tokens.shadow.lg,
            },
          },
          MuiDialogTitle: {
            root: {
              borderBottom: `1px solid ${tokens.border}`,
              padding: "1.125rem 1.5rem",
              "& .MuiTypography-root": {
                fontSize: fontSize.lg,
                fontWeight: fontWeight.medium,
                letterSpacing: letterSpacing.caps,
                textTransform: "uppercase",
              },
            },
          },
          MuiDialogContent: {
            root: { padding: "1.5rem" },
          },
          MuiDialogActions: {
            root: {
              borderTop: `1px solid ${tokens.border}`,
              padding: "1rem 1.5rem",
              gap: 8,
            },
          },
        },
      },
      // Locale pt-BR dos componentes MUI (paginacao, tabelas, datas).
      ptBR),
    [darkMode, tokens]
  );

  const contextValue = useMemo(
    () => ({ darkMode, toggleTheme, tokens }),
    [darkMode, toggleTheme, tokens]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
